import {
  DefaultAlertDialog,
  DefaultModalDialog,
  DefaultScreenTileComponentBox,
  DistanceBetween,
  DurationViewer,
  LabeledText,
  PictureViewer,
  Option,
  Select,
  useToast,
} from './defaultUIComponents';
import {
  Box,
  Checkbox,
  Flex,
  Grid,
  GridItem,
  HStack,
  VStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import {
  useCopyJobToProject,
  useMergeJobs,
} from '~/subdomains/ProjectOverview/Project/utils';
import ChangeRequestApi from '~/common/api/zeiterfassungServerApi/ChangeRequestApi';
import { ChangeRequest } from '~/common/entities/ChangeRequest/ChangeRequestType';
import { DayplanApi } from '~/common/api/zeiterfassungServerApi/DayplanApi';
import { CopyIcon, DeleteIcon, EditIcon, LinkIcon } from '@chakra-ui/icons';
import ProjectApi from '~/common/api/zeiterfassungServerApi/ProjectApi';
import DepartmentApi from '../api/zeiterfassungServerApi/DepartmentApi';
import { PictureType } from '~/common/entities/Pictures/PictureTypes';
import { Job, Project } from '~/common/entities/Project/ProjectTypes';
import { JobApi } from '~/common/api/zeiterfassungServerApi/JobApi';
import { Todo } from '~/common/entities/Dayplan/DayplanTypes';
import { useQueryClient } from '@tanstack/react-query';
import routes from '~/common/constants/routes.json';
import useTranslate from '~/common/utils/translate';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';

interface JobTileInput {
  job: Job;
  project?: Project;
  employeeId?: string;
  todo?: Todo;
  todoAmount?: number;
  markThisJob?: boolean;
  noIcons?: boolean;
  deleteStep?: boolean;
  copyStep?: boolean;
  changeStepnumber?: boolean;
  linkSteps?: boolean;
  projectId?: number;
  changeRequest?: ChangeRequest | null;
  secondMergeJobIn?: number;
  newStepnumberIn?: number;
}

const JobTile: React.FC<JobTileInput> = ({
  job: paramJob,
  project,
  employeeId,
  todo,
  todoAmount = 0,
  markThisJob = false,
  deleteStep = false,
  copyStep = false,
  changeStepnumber = false,
  linkSteps = false,
  projectId = -1,
  changeRequest = null,
  secondMergeJobIn = -1,
  newStepnumberIn = -1,
}: JobTileInput) => {
  const navigate = useNavigate();
  const [showDelete, setShowDelete] = useState(false);
  const [showCopy, setShowCopy] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [showChangeStepnumber, setShowChangeStepnumber] = useState(false);
  const [openPictures, setOpenPictures] = useState(false);
  const translate = useTranslate();
  const { copyJob } = useCopyJobToProject();
  const { mergeJobs } = useMergeJobs();
  const queryClient = useQueryClient();
  const [newProjectId, setNewProjectId] = useState(projectId);
  const [newStepnumber, setNewStepnumber] = useState(newStepnumberIn);
  const [newDepartment, setNewDepartment] = useState(paramJob?.department?.id);
  const [{ data: projects }] = ProjectApi.hooks.useProjectsQuery({
    enabled: showCopy,
  });
  const toast = useToast({});

  const [{ data: departments }] = DepartmentApi.hooks.useDepartmentsQuery();

  const { mutateAsync: deleteChangeRequest } =
    ChangeRequestApi.hooks.useDeleteChangeRequestMutation();
  const { mutateAsync: editJob } = JobApi.hooks.useEditJobMutation();
  const [projectQuery] = ProjectApi.hooks.useProjectQuery({
    id: project?.id,
  });
  const { data: projectData } = projectQuery;

  const job: Job | undefined = useMemo(() => {
    if (project) {
      const jobFromProject = projectData?.jobs?.find(
        (j) => j.id === paramJob.id,
      );
      return jobFromProject || paramJob;
    } else if (todo) {
      return todo.job;
    } else {
      return undefined;
    }
  }, [project, projectData, todo, paramJob]);

  const toggleShowDelete = () => {
    setShowDelete(!showDelete);
  };
  const [checkedlist, setCheckedlist] = useState([job?.id, secondMergeJobIn]);

  const toggleShowChangeStepnumber = () => {
    setShowChangeStepnumber(!showChangeStepnumber);
  };

  const toggleShowMerge = () => {
    setShowMerge(!showMerge);
  };

  const { mutate: deleteTodo } = DayplanApi.hooks.useDeleteTodoMutation(
    employeeId!,
  );

  const toggleShowCopy = () => {
    setShowCopy(!showCopy);
  };

  const toggleOpenPictures = () => {
    setOpenPictures(!openPictures);
  };

  const onAcceptModalAction = () => {
    void deleteChangeRequest(changeRequest);
  };

  const onDeleteModalAction = () => {
    if (project) {
      if (job) {
        void JobApi.mutations.deleteJob(job).then(() => {
          toggleShowDelete();
          void queryClient.invalidateQueries(['CHANGE_REQUESTS']);
        });
      }
    } else if (todo) {
      if (employeeId) {
        void deleteTodo(todo, {
          onSuccess: () => {
            toggleShowDelete();
            void queryClient.invalidateQueries(['CHANGE_REQUESTS']);
          },
        });
      }
    }
  };
  const onCopyModalAction = async () => {
    if (job) {
      try {
        await copyJob({ new_project_id: newProjectId, job_id: job.id });
        toggleShowCopy();
        onAcceptModalAction();
      } catch (e) {
        toast({ title: 'generic.error', status: 'error' });
      }
    }
  };

  const onMergeModalAction = async () => {
    try {
      if (checkedlist) {
        await mergeJobs({ jobIds: checkedlist as number[] });
        toggleShowMerge();
        navigate(
          routes.EDIT_JOB.replace(
            ':id',
            `${checkedlist[0] as unknown as string}`,
          ),
        );
      }
    } catch (e) {
      toast({ title: 'generic.error', status: 'error' });
    }
  };

  const onChangeStepnumberModalAction = async () => {
    try {
      if (!(newStepnumber == -1)) {
        if (job && project) {
          await editJob({
            ...job,
            department: newDepartment,
            duration: job.duration as number,
            projects: job.projects
              ? (_.map(job.projects, (project) => project.id) ?? [])
              : [project?.id],
            step_number: newStepnumber,
          });
          toggleShowChangeStepnumber();
          onAcceptModalAction();
        }
      }
    } catch (e) {
      toast({ title: 'generic.error', status: 'error' });
    }
  };

  const containsTodoAmount = todoAmount > 0;

  if (!job) {
    return null;
  }

  return (
    <DefaultScreenTileComponentBox
      key={job.id}
      distanceBetweenTileComponents={DistanceBetween.narrow}
      minWidth="100%"
      w="100%"
      backgroundColor={markThisJob ? 'red.100' : '#DEDEDE'}
    >
      <HStack>
        <Flex flex={5}>
          <Grid templateColumns="repeat(3, 1fr)" gap={0} width="100%">
            {/* first row */}
            <GridItem colSpan={1}>
              <LabeledText
                label="jobTile.stepNumber.label"
                labelParams={[job.step_number.toString()]}
                mr={5}
              />
            </GridItem>
            <GridItem colSpan={2}>
              <LabeledText label="jobTile.name.label" mr={5}>
                {job.name}
              </LabeledText>
            </GridItem>
            {/* second row */}
            <GridItem colSpan={1}>
              <DurationViewer
                durationInSeconds={job.duration as number}
                showSeconds
                label="jobTile.duration.label"
                mr={5}
              />
            </GridItem>
            <GridItem colSpan={1}>
              <LabeledText label="jobTile.donePerProject.label" mr={5}>
                {job.done_per_project > 0 ? job.done_per_project : 0}
              </LabeledText>
            </GridItem>
            <GridItem colSpan={1}>
              {!!job.department && job.department.name !== '' ? (
                <LabeledText label="jobTile.department.label">
                  {job.department?.name}
                </LabeledText>
              ) : null}
            </GridItem>
            {/* third row */}
            <GridItem colSpan={3}>
              <LabeledText label="jobTile.description.label" mr={5}>
                {job.description}
              </LabeledText>
            </GridItem>{' '}
            <GridItem colSpan={1}>
              {containsTodoAmount ? (
                <LabeledText label="jobTile.todoAmount.label" mr={5}>
                  {todoAmount}
                </LabeledText>
              ) : null}
            </GridItem>
          </Grid>
        </Flex>
        {copyStep && (
          <Flex flex={1}>
            <Tooltip
              label={translate('jobTile.copyStep.tooltip.label')}
              aria-label={translate('jobTile.copyStep.tooltip.label')}
            >
              <Box
                _hover={{ cursor: 'pointer' }}
                onClick={() => toggleShowCopy()}
              >
                <CopyIcon boxSize={6} />
              </Box>
            </Tooltip>
          </Flex>
        )}
        {deleteStep && (
          <Flex flex={1}>
            <Tooltip
              label={translate('jobTile.deleteJob.tooltip.label')}
              aria-label={
                translate('jobTile.deleteJob.tooltip.label') as string
              }
            >
              <IconButton
                icon={<DeleteIcon />}
                onClick={toggleShowDelete}
                aria-label={
                  translate('jobTile.deleteJob.tooltip.label') as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
          </Flex>
        )}

        {changeStepnumber && (
          <Flex flex={1}>
            <Tooltip
              label={translate('jobTile.changeStepnumber.Job.tooltip.label')}
              aria-label={
                translate(
                  'jobTile.changeStepnumber.Job.tooltip.label',
                ) as string
              }
            >
              <IconButton
                icon={<EditIcon />}
                onClick={toggleShowChangeStepnumber}
                aria-label={
                  translate(
                    'jobTile.changeStepnumber.Job.tooltip.label',
                  ) as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
          </Flex>
        )}
        {linkSteps && (
          <Flex flex={1}>
            <Tooltip
              label={translate('jobTile.mergeJobs.Job.tooltip.label')}
              aria-label={
                translate('jobTile.mergeJobs.Job.tooltip.label') as string
              }
            >
              <IconButton
                icon={<LinkIcon />}
                onClick={toggleShowMerge}
                aria-label={
                  translate('jobTile.mergeJobs.Job.tooltip.label') as string
                }
                isRound
                mr={1}
              />
            </Tooltip>
          </Flex>
        )}

        {!deleteStep && !copyStep && !changeStepnumber && !linkSteps && (
          <Flex flex={1}></Flex>
        )}
        <DefaultAlertDialog
          dialogHeaderText="jobTile.deleteJob.dialogHeaderText"
          dialogBodyText="jobTile.deleteJob.dialogBodyText"
          isOpen={showDelete && deleteStep}
          onConfirmedAction={onDeleteModalAction}
          onCancel={toggleShowDelete}
        />
        <DefaultModalDialog
          dialogHeaderText="jobTile.copyJob.dialogHeaderText"
          submitText="projectTile.duplicateProject.submitText"
          isOpen={showCopy && copyStep}
          onCancel={() => setShowCopy(!showCopy)}
          disableSubmitButton={newProjectId === -1}
          onSubmit={onCopyModalAction}
          showCancelButton
          size="xl"
          initialValues={{ newProjectID: '' }}
        >
          <Select
            placeholder={translate('jobListItem.duplicateJob.InputText')}
            onChange={(e) => setNewProjectId(parseInt(e.currentTarget.value))}
            w="100%"
            ml={60}
            mb={2}
          >
            {projects &&
              _.orderBy(projects, ['name'], ['asc']).map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.name}
                </Option>
              ))}
          </Select>{' '}
        </DefaultModalDialog>

        <DefaultModalDialog
          dialogHeaderText="projectTile.mergeJobs.dialogHeaderText"
          submitText="projectTile.mergeJobs.dialogHeaderText"
          isOpen={showMerge}
          onCancel={() => {
            setCheckedlist([job.id, secondMergeJobIn]);
            setShowMerge(!showMerge);
          }}
          onSubmit={onMergeModalAction}
          showCancelButton
          size="xl"
          initialValues={{ jobIds: [] }}
        >
          <VStack alignItems="start">
            {project &&
              project.jobs?.map((j) => (
                <GridItem key={j.id}>
                  <Checkbox
                    size="lg"
                    colorScheme="blue"
                    isChecked={j.id == job.id || checkedlist.includes(j.id)}
                    onChange={() => setCheckedlist([checkedlist[0], j.id])}
                    isDisabled={j.id == job.id}
                    name="jobIds"
                    value={j.id}
                  >
                    {j.name}
                  </Checkbox>
                </GridItem>
              ))}
          </VStack>
        </DefaultModalDialog>
        <DefaultModalDialog
          dialogHeaderText="jobTile.changeStepnumber.dialogHeaderText"
          submitText="jobTile.changeStepnumber.submitText"
          isOpen={showChangeStepnumber && changeStepnumber}
          onCancel={() => setShowChangeStepnumber(!showChangeStepnumber)}
          disableSubmitButton={newStepnumber === -1}
          onSubmit={onChangeStepnumberModalAction}
          showCancelButton
          size="xl"
          initialValues={{ newProjectId: '' }}
        >
          <Select
            placeholder={translate(
              'jobTile.changeStepnumber.select.placeholder',
            )}
            onChange={(e) => setNewStepnumber(parseInt(e.currentTarget.value))}
            w="100%"
            ml={60}
            mb={2}
          >
            {project?.jobs &&
              _.orderBy(project.jobs, ['id'], ['asc']).map((j) => (
                <Option key={j.id} value={j.step_number}>
                  {j.step_number}
                </Option>
              ))}
          </Select>{' '}
          <Select
            label="jobTile.changeStepnumber.select.department.heading"
            value={newDepartment}
            onChange={(e) => setNewDepartment(parseInt(e.currentTarget.value))}
            w="100%"
            ml={60}
            mb={2}
          >
            {departments &&
              _.orderBy(departments, ['id'], ['asc']).map((department) => (
                <Option key={department.id} value={department.id}>
                  {department.name}
                </Option>
              ))}
          </Select>{' '}
        </DefaultModalDialog>
        {job.job_pictures.length > 0 && (
          <Flex flex={1}>
            <GridItem colSpan={2} rowSpan={2} mt={2}>
              <PictureViewer
                pictureIds={job.job_pictures}
                projectOrJobId={job.id.toString()}
                pictureType={PictureType.JOB}
                showInModal={false}
                mt={2}
                mr={0}
                cursor="pointer"
                onClick={toggleOpenPictures}
                margin={0}
              />
              <DefaultModalDialog
                dialogHeaderText="jobTile.showPictures.dialogHeaderText"
                submitText="jobTile.showPictures.submitText"
                isOpen={openPictures}
                onCancel={toggleOpenPictures}
                onSubmit={toggleOpenPictures}
                showCancelButton={false}
                size="4xl"
                initialValues={{}}
              >
                <PictureViewer
                  pictureIds={job.job_pictures}
                  projectOrJobId={job.id.toString()}
                  pictureType={PictureType.JOB}
                  closeModal={toggleOpenPictures}
                />
              </DefaultModalDialog>
            </GridItem>
          </Flex>
        )}
        {job.job_pictures.length === 0 && <Flex flex={1}></Flex>}
      </HStack>
    </DefaultScreenTileComponentBox>
  );
};

export default JobTile;
